import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LOGO_URL, BRAND } from "@/lib/api";
import usePageMeta from "@/lib/usePageMeta";
import { Compass, ShieldCheck, Handshake, ClockClockwise, ArrowRight } from "@phosphor-icons/react";

const values = [
  { Icon: Compass, title: "Always show up", text: "On-time, every time. If we're running late, you'll know before we are." },
  { Icon: ShieldCheck, title: "Quality you can see", text: "Workmanship guaranteed in writing. Tidy, professional, properly finished." },
  { Icon: Handshake, title: "Straight talk", text: "Honest quotes, clear scope. No surprises on the invoice, ever." },
  { Icon: ClockClockwise, title: "Fast & flexible", text: "Same-week bookings. Emergency call-outs when you really need us." },
];

export default function About() {
  usePageMeta({ title: "About", path: "/about", description: "Locally owned property maintenance and handyman across Albury and Wodonga. Tidy. On time. Properly finished." });
  return (
    <div data-testid="about-page">
      <section data-nav-theme="dark" className="bg-navy-deep text-white py-20 relative overflow-hidden">
        <div className="hero-grain absolute inset-0 opacity-30" />
        <div className="absolute -right-20 -top-20 w-96 h-96 opacity-[0.06]"><img src={LOGO_URL} alt="" className="w-full h-full object-contain compass-rotate"/></div>
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="eyebrow">About us</div>
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl  mt-3 leading-tight">Locally owned. Locally trusted.</h1>
            <p className="mt-5 text-slate-300 leading-relaxed max-w-xl">True North Maintenance was founded to bring back the kind of tradesperson you can actually count on. Reliable communication, quality workmanship, and a deep care for the homes we look after.</p>
          </div>
          <div className="bg-white text-navy p-7 rounded-sm border-l-2 border-navy">
            <div className="eyebrow eyebrow-dark !text-slate-500">Quick facts</div>
            <ul className="mt-3 space-y-3 text-sm">
              <li className="flex justify-between gap-3"><span className="text-slate-500">Service area</span><span className="font-semibold text-right">{BRAND.area}</span></li>
              <li className="flex justify-between gap-3"><span className="text-slate-500">Customer rating</span><span className="font-semibold text-right">4.9 / 5</span></li>
              <li className="flex justify-between gap-3"><span className="text-slate-500">Trusted by</span><span className="font-semibold text-right">The Good Guys &amp; JB HI-FI</span></li>
              <li className="flex justify-between gap-3"><span className="text-slate-500">Free quotes</span><span className="font-semibold text-right">Always, no obligation</span></li>
            </ul>
          </div>
        </div>
      </section>
      <section data-nav-theme="light" className="max-w-7xl mx-auto px-5 md:px-8 py-20">
        <div className="grid md:grid-cols-2 gap-4">
          {values.map((v, i) => (
            <div key={i} className="bg-white border border-slate-200 p-7 rounded-sm">
              <div className="w-11 h-11 bg-navy-deep text-white flex items-center justify-center rounded-sm"><v.Icon size={22} weight="duotone"/></div>
              <h3 className="font-display text-2xl tracking-wide text-navy mt-4">{v.title}</h3>
              <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{v.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-14 bg-navy text-white p-10 rounded-sm text-center">
          <h3 className="font-display text-3xl">Built on a simple promise.</h3>
          <p className="mt-2 text-slate-300">Find your true north — a maintenance partner that listens, turns up, and finishes the job properly. That's it. That's the whole thing.</p>
          <Link to="/quote"><Button className="pill-navy mt-5">Work with us <ArrowRight size={16} className="ml-2"/></Button></Link>
        </div>
      </section>
    </div>
  );
}
